import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useActiveReceiptCategories, useReceiptConcepts, useReceipt } from '../hooks/useReceipts';
import { IssueReceiptForm } from './IssueReceiptForm';
import { ReceiptPrintPreview } from './ReceiptPrintPreview';
import { receiptToPrintData } from '../types';
import { publicReceiptUrl } from '../utils/publicReceiptUrl';
import { AFFILIATION_CATEGORY_CODE } from '../utils/affiliation';

export interface AffiliationMinister {
  id: string;
  adb_code: string;
  full_name: string | null;
  dni: string | null;
  /** When the minister was registered; used as the default affiliation date (editable in the form). */
  created_at: string;
}

interface Props {
  minister: AffiliationMinister;
  districtName?: string | null;
  regionName?: string | null;
  /** Skip the "issue a receipt?" question and open the form straight away. */
  skipPrompt?: boolean;
  onClose: () => void;
}

/** Afiliación receipt for a minister: ask → prefilled form → print preview. Nothing is issued automatically. */
export function AffiliationReceiptFlow({ minister, districtName, regionName, skipPrompt, onClose }: Props) {
  const [stage, setStage] = useState<'prompt' | 'form' | 'preview'>(skipPrompt ? 'form' : 'prompt');
  const [issuedId, setIssuedId] = useState<string | null>(null);

  const { data: categories, isLoading: loadingCategories } = useActiveReceiptCategories();
  const { data: concepts = [] } = useReceiptConcepts();
  const { data: issued } = useReceipt(issuedId ?? undefined);

  const category = categories?.find((c) => c.code === AFFILIATION_CATEGORY_CODE);
  const concept = concepts.find((c) => c.code === 'AFILIACION');
  const name = minister.full_name ?? minister.adb_code;

  // No Afiliación category configured: nothing to offer.
  useEffect(() => {
    if (!loadingCategories && !category) onClose();
  }, [loadingCategories, category, onClose]);

  if (!category) return null;

  if (stage === 'preview') {
    return issued ? (
      <ReceiptPrintPreview
        data={receiptToPrintData(issued, publicReceiptUrl(issued.public_token))}
        onClose={onClose}
      />
    ) : null;
  }

  if (stage === 'form') {
    return (
      <IssueReceiptForm
        lockedCategoryId={category.id}
        initialPayerName={name}
        initialPayerCi={minister.dni ?? undefined}
        ministerId={minister.id}
        initialAffiliation={{
          adb_code: minister.adb_code,
          affiliation_date: minister.created_at.slice(0, 10),
          district: districtName ?? '',
          region: regionName ?? '',
        }}
        initialLines={[{ concept_id: concept?.id ?? '', description: 'Afiliación', quantity: '1', unit_price: '' }]}
        onClose={onClose}
        onIssued={(r) => { setIssuedId(r.id); setStage('preview'); }}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>¿Emitir recibo de afiliación?</DialogTitle>
          <DialogDescription>
            {name} ({minister.adb_code}). Se abrirá el recibo con sus datos ya completados.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Ahora no</Button>
          <Button type="button" onClick={() => setStage('form')}>Emitir recibo</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
